import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, ArrowLeft, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationsContext";
import { useChatSocket } from "../hooks/useChatSocket";
import VerifiedBadge from "../components/VerifiedBadge";

function profileImage(profile) {
  if (!profile) return null;
  if (profile.photos?.length > 0) return fileUrl(profile.photos[0]);
  if (profile.photo_url) return profile.photo_url;
  return null;
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function Chat() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshUnread } = useNotifications();

  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  const listRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  const loadAll = useCallback(async () => {
    try {
      const [convRes, msgsRes, subRes] = await Promise.all([
        api.get(`/conversations/${matchId}`),
        api.get(`/conversations/${matchId}/messages`),
        api.get("/subscription/me"),
      ]);
      setConv(convRes.data);
      setMessages(msgsRes.data);
      setUsage(subRes.data);
      // Mark as read
      api.post(`/conversations/${matchId}/read`).then(refreshUnread);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Conversation not found");
        navigate("/messages");
      }
    } finally {
      setLoading(false);
    }
  }, [matchId, navigate, refreshUnread]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (!loading) scrollToBottom(); }, [loading, messages.length]);

  // Real-time inbound messages
  useChatSocket(useCallback((payload) => {
    if (payload?.type !== "message") return;
    const m = payload.data;
    if (m.match_id !== matchId) return;
    setMessages((prev) => {
      if (prev.some((x) => x.message_id === m.message_id)) return prev;
      return [...prev, m];
    });
    // If we're viewing this convo, mark inbound as read immediately
    if (m.recipient_id === user?.user_id) {
      api.post(`/conversations/${matchId}/read`).then(refreshUnread).catch(() => {});
    }
  }, [matchId, user, refreshUnread]));

  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${matchId}/messages`, { text });
      setMessages((prev) => prev.some((x) => x.message_id === data.message_id) ? prev : [...prev, data]);
      setDraft("");
      // Refresh usage
      api.get("/subscription/me").then((r) => setUsage(r.data));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402) {
        toast.error(typeof detail === "string" ? detail : "Daily limit reached. Upgrade to Premium.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't send message");
      }
    } finally {
      setSending(false);
    }
  };

  if (loading || !conv) {
    return <div className="p-12 text-center text-gold-soft tracking-overline text-xs">LOADING CONVERSATION</div>;
  }

  const otherProfile = conv.profile;
  const img = profileImage(otherProfile);

  const isFree = usage?.tier === "free";
  const limit = usage?.messages_limit_per_day ?? 10;
  const used = usage?.messages_used_today ?? 0;
  const remaining = isFree ? Math.max(0, limit - used) : -1;
  const atLimit = isFree && remaining <= 0;

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 md:py-8" data-testid="chat-page">
      <div className="glass-strong rounded-3xl overflow-hidden flex flex-col h-[calc(100vh-9rem)] md:h-[calc(100vh-10rem)]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
          <button
            onClick={() => navigate("/messages")}
            className="md:hidden w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-white/70"
            data-testid="chat-back-btn"
          >
            <ArrowLeft size={18} />
          </button>
          {img ? (
            <img src={img} alt={otherProfile.name} className="w-11 h-11 rounded-full object-cover" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-[#1a1a1a] flex items-center justify-center">
              <span className="font-display text-base text-gold/50">{otherProfile.name?.[0]}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg leading-tight flex items-center gap-1.5">
              {otherProfile.name}, <span className="text-white/60 text-base">{otherProfile.age}</span>
              {otherProfile.verified && <VerifiedBadge size={14} />}
            </div>
            <div className="text-xs text-white/40 tracking-wide truncate">{otherProfile.location}</div>
          </div>
        </div>

        {/* Messages list */}
        <div ref={listRef} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-3" data-testid="chat-messages-list">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-8">
              <div className="w-14 h-14 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-gold mb-4">
                <Sparkles size={22} />
              </div>
              <div className="font-display text-2xl font-light mb-2">You matched with {otherProfile.name}.</div>
              <div className="text-white/55 max-w-sm leading-relaxed">
                Take a breath. Say something honest — they're already curious.
              </div>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_id === user.user_id;
              return (
                <motion.div
                  key={m.message_id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-bubble-${mine ? "me" : "them"}`}
                >
                  <div className={`max-w-[78%] px-4 py-3 rounded-2xl ${
                    mine
                      ? "bg-[#D4AF37] text-[#050505] rounded-br-md"
                      : "bg-white/[0.06] text-white border border-white/10 rounded-bl-md"
                  }`}>
                    <div className="text-[15px] leading-relaxed font-light whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[10px] mt-1 tracking-wide ${mine ? "text-[#050505]/60" : "text-white/40"}`}>
                      {formatTime(m.created_at)}
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Limit banner */}
        {isFree && (
          <div className="px-5 py-2.5 border-t border-white/10 flex items-center justify-between text-xs">
            <div className="text-white/50 flex items-center gap-2">
              <Lock size={12} className="text-gold-soft" />
              <span>{atLimit ? "Daily limit reached." : `${remaining} of ${limit} messages left today.`}</span>
            </div>
            <Link to="/premium" className="text-gold hover:text-gold-soft tracking-wide" data-testid="goto-premium">Upgrade →</Link>
          </div>
        )}

        {/* Composer */}
        <form onSubmit={send} className="p-4 border-t border-white/10 flex items-center gap-3" data-testid="chat-composer">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={atLimit ? "Upgrade to keep the conversation going..." : "Write something thoughtful..."}
            disabled={atLimit}
            className="input-luxe flex-1 disabled:opacity-50"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={sending || !draft.trim() || atLimit}
            className="w-12 h-12 rounded-full bg-[#D4AF37] text-[#050505] flex items-center justify-center hover:bg-[#E6C96C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
            data-testid="chat-send-btn"
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}
